/**
 * ProfileGradientHeader — Shared gradient hero header for profile pages.
 *
 * Renders the purple-to-teal gradient background that spans full viewport
 * width, with a centered column layout containing: action buttons (top-right),
 * avatar, display name, meta line, decorative divider, and bio/vibe text.
 *
 * Used by CommunityProfilePage and CommunityProfileSettingsPage so the
 * gradient header styles live in one place.
 *
 * @shared CommunityProfileSettingsPage, CommunityProfilePage
 */

import React from 'react'
import { H1 } from '../../../../Heading/index'
import { useTranslatedText } from '../../../../StaticText/StaticTextContext'

export interface ProfileGradientHeaderProps {
  /** Render slot for the avatar area — typically ProfileAvatarRing, or
   *  a button wrapping it with a camera overlay for settings */
  avatarContent: React.ReactNode
  /** User's display name rendered as an H1 */
  displayName: string
  /** Optional action buttons rendered in the responsive actions row */
  actionButtons?: React.ReactNode
  /** Optional meta line content — typically a <ProfileMetaLine> component */
  metaLine?: React.ReactNode
  /** Optional bio text displayed below the decorative divider */
  bio?: string | null
  /** Whether to show the bio (defaults to true when bio is truthy) */
  showBio?: boolean
  /** Optional social line content rendered after the meta line */
  socialLine?: React.ReactNode
  /** Additional className on the root element for context-specific overrides */
  className?: string
}

function ProfileGradientHeader ({
  avatarContent,
  displayName,
  actionButtons,
  metaLine,
  socialLine,
  bio,
  showBio,
  className,
}: ProfileGradientHeaderProps) {
  const showBioResolved = showBio !== undefined ? showBio : !!bio

  // Bio is wrapped in locale-appropriate quotation marks. Sourced from the
  // surrounding static-text Provider so non-Latin locales can ship the right
  // glyphs (de „"  fr « »  etc.). This component is currently rendered with
  // bio only by CommunityProfilePage, so the keys live in that slice.
  const bioOpenQuote = useTranslatedText({ keys: ['bioOpenQuote'] })
  const bioCloseQuote = useTranslatedText({ keys: ['bioCloseQuote'] })

  return (
    <div className={`profile-gradient-header${className ? ` ${className}` : ''}`}>
      {/* Action buttons: absolutely positioned top-right */}
      {actionButtons ? (
        <div className="profile-gradient-header__actions">
          {actionButtons}
        </div>
      ) : null}

      {/* Hero: avatar + name/meta — row on mobile, column on desktop */}
      <div className="profile-gradient-header__hero">
        <div className="profile-gradient-header__avatar-ring">
          {avatarContent}
        </div>
        <div className="profile-gradient-header__info">
          <H1 className="profile-gradient-header__display-name">{displayName}</H1>
          {metaLine ? (
            <div className="profile-gradient-header__meta">
              {metaLine}
            </div>
          ) : null}
          {socialLine ? (
            <div className="profile-gradient-header__social">
              {socialLine}
            </div>
          ) : null}
        </div>
      </div>

      {/* Decorative divider between meta and bio */}
      {bio && showBioResolved ? (
        <div className="profile-gradient-header__divider" aria-hidden="true">
          <span className="profile-gradient-header__divider-line" />
          <span className="profile-gradient-header__divider-icon">
            {/* Placeholder star — replace with actual star-ai SVG icon when provided */}
            {'\u2726'}
          </span>
          <span className="profile-gradient-header__divider-line" />
        </div>
      ) : null}

      {/* Bio / Vibe text */}
      {bio && showBioResolved ? (
        <p className="profile-gradient-header__bio">
          {`${bioOpenQuote}${bio}${bioCloseQuote}`}
        </p>
      ) : null}
    </div>
  )
}

export default React.memo(ProfileGradientHeader)
